import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { FiPhone, FiMail, FiMapPin, FiHome, FiArrowRight } from 'react-icons/fi';
import { useSettings } from '../../contexts/SettingsContext';
import { Container } from '../ui';
import { hasTrackingTags, isConsentRequired, openConsentPreferences } from '../../utils/tracking';

const Wrapper = styled.footer`
  background: ${({ theme }) => theme.colors.neutral[900]};
  color: ${({ theme }) => theme.colors.neutral[300]};
  margin-top: 4rem;
`;

const Top = styled(Container)`
  display: grid;
  grid-template-columns: 1.6fr 1fr 1.4fr;
  gap: 3rem;
  padding-top: 3.5rem;
  padding-bottom: 3rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
`;

const BrandMark = styled.span`
  width: 38px;
  height: 38px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BrandName = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  font-size: 1.15rem;
  color: #fff;
`;

const Desc = styled.p`
  color: ${({ theme }) => theme.colors.neutral[400]};
  line-height: 1.7;
  max-width: 38ch;
  font-size: 0.95rem;
`;

const ColTitle = styled.h4`
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 1.1rem;
`;

const FList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const footerLink = css`
  color: ${({ theme }) => theme.colors.neutral[400]};
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: #fff;
    transform: translateX(2px);
  }
`;

const FLink = styled(Link)`
  ${footerLink}
`;

const FButton = styled.button`
  ${footerLink}
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: 0.95rem;
  cursor: pointer;
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: ${({ theme }) => theme.colors.neutral[400]};
  font-size: 0.95rem;
  margin-bottom: 0.85rem;
  transition: color 0.2s ease;

  svg {
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
  &:hover {
    color: #fff;
  }
`;

const CtaCard = styled.div`
  margin-top: 1.25rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 1.1rem 1.25rem;
`;

const CtaLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;

  svg {
    transition: transform 0.2s ease;
  }
  &:hover svg {
    transform: translateX(3px);
  }
`;

const Bottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const BottomInner = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.neutral[500]};
  flex-wrap: wrap;
`;

const Footer: React.FC = () => {
  const { siteSettings } = useSettings();

  const companyName =
    siteSettings?.company_info?.name || siteSettings?.site_name || 'Imobiliária';
  const phone = siteSettings?.company_info?.phone;
  const email = siteSettings?.company_info?.email;
  const addr = siteSettings?.company_info?.address;
  const structuredAddress = addr
    ? [
        [addr.street, addr.number].filter(Boolean).join(', '),
        addr.neighborhood,
        [addr.city, addr.state].filter(Boolean).join('/'),
      ].filter(Boolean).join(' - ')
    : undefined;
  const address = siteSettings?.contact_address_full || structuredAddress;
  const description =
    siteSettings?.site_description ||
    'Assessoria imobiliária completa para você comprar, vender ou alugar com segurança e tranquilidade.';
  const year = '2026';

  return (
    <Wrapper>
      <Top>
        <div>
          <Brand to="/">
            <BrandMark>
              <FiHome size={20} />
            </BrandMark>
            <BrandName>{companyName}</BrandName>
          </Brand>
          <Desc>{description}</Desc>
        </div>

        <div>
          <ColTitle>Navegação</ColTitle>
          <FList>
            <li>
              <FLink to="/">Início</FLink>
            </li>
            <li>
              <FLink to="/imoveis">Imóveis</FLink>
            </li>
            <li>
              <FLink to="/mapa">Imóveis no mapa</FLink>
            </li>
            <li>
              <FLink to="/sobre">Sobre nós</FLink>
            </li>
            <li>
              <FLink to="/blog">Blog</FLink>
            </li>
            <li>
              <FLink to="/contato">Contato</FLink>
            </li>
            <li>
              <FLink to="/politica-de-privacidade">Política de Privacidade</FLink>
            </li>
            {hasTrackingTags() && isConsentRequired() && (
              <li>
                <FButton type="button" onClick={openConsentPreferences}>
                  Preferências de cookies
                </FButton>
              </li>
            )}
          </FList>
        </div>

        <div>
          <ColTitle>Contato</ColTitle>
          {phone && (
            <ContactItem href={`tel:${phone.replace(/\D/g, '')}`}>
              <FiPhone size={16} /> {phone}
            </ContactItem>
          )}
          {email && (
            <ContactItem href={`mailto:${email}`}>
              <FiMail size={16} /> {email}
            </ContactItem>
          )}
          {address && (
            <ContactItem as="div">
              <FiMapPin size={16} /> {address}
            </ContactItem>
          )}
          <CtaCard>
            <CtaLink to="/anunciar">
              Quer anunciar seu imóvel? <FiArrowRight size={16} />
            </CtaLink>
          </CtaCard>
        </div>
      </Top>

      <Bottom>
        <BottomInner>
          <span>
            © {year} {companyName}. Todos os direitos reservados.
          </span>
          <span>Creci ativo · Atendimento com responsabilidade</span>
        </BottomInner>
      </Bottom>
    </Wrapper>
  );
};

export default Footer;
